import { Box } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ReactComponent as CheckCircle } from 'src/assets/tailwind/check-circle.svg';
import { ReactComponent as CrossCircle } from 'src/assets/tailwind/cross-circle.svg';
import { ReactComponent as Eye } from 'src/assets/tailwind/eye.svg';
import { ReactComponent as EyeOff } from 'src/assets/tailwind/eye-off.svg';
import SigninBg from 'src/assets/tailwind/signinBg.svg';
import { InfoContainer } from 'src/components/Signup/InfoContainer';
import { LanguageDropdown } from 'src/components/Signup/LanguageDropdown';
import { Logo } from 'src/components/Signup/Logo/Logo';
import { Button } from 'src/components/ui/button';
import { RHFInput } from 'src/components/ui/RHF/RHFInput';
import { Typography } from 'src/components/ui/typography';
import { POST_DOMAIN_PART } from 'src/config';
import { LOADING_STEPS, SCHEME } from 'src/content/SignUp/utils';
import useAnalytics from 'src/hooks/useAnalytics';
import { SIGNUP_EMAIL_VERIFIED } from 'src/utils/analyticsUtils';
import { TRANSLATION_CONSTANTS as T } from 'src/utils/translations';

import ErrorOutlineOutlinedIcon from '../../../assets/alert-circle.svg';
import type { PasswordSettingFormValues } from './schema';
import { usePasswordSetting } from './usePasswordSetting';

const withClick =
  (Icon: React.FC<React.SVGProps<SVGSVGElement>>, onClick: () => void) =>
  (props: React.SVGProps<SVGSVGElement>) =>
    <Icon {...props} onClick={onClick} className="tw:cursor-pointer" />;

export type PasswordSettingProps = {
  state: string;
  email: string;
  token: string;
};

export const PasswordSetting = ({
  state,
  email,
  token
}: PasswordSettingProps) => {
  const {
    methods,
    isVerifying,
    isPasswordEnabled,
    isLoading,
    isSuggested,
    domainStatus,
    domainError,
    passwordCriteria,
    onSubmit
  } = usePasswordSetting({ token, email, state });

  const { t } = useTranslation();
  const collectAnalytics = useAnalytics();
  const [stepIndex, setStepIndex] = useState<number>(0);
  const [hidePassword, setHidePassword] = useState(false);
  const [hideConfirm, setHideConfirm] = useState(false);

  useEffect(() => {
    collectAnalytics(SIGNUP_EMAIL_VERIFIED);
  }, []);

  useEffect(() => {
    if (isLoading) {
      const interval = setInterval(() => {
        setStepIndex((prev) =>
          prev < LOADING_STEPS.length - 1 ? prev + 1 : prev
        );
      }, 5000);

      return () => clearInterval(interval);
    } else {
      setStepIndex(0);
    }
  }, [isLoading]);

  const passwordIcon = useMemo(
    () =>
      withClick(hidePassword ? EyeOff : Eye, () =>
        setHidePassword((prev) => !prev)
      ),
    [hidePassword]
  );

  const confirmIcon = useMemo(
    () =>
      withClick(hideConfirm ? EyeOff : Eye, () =>
        setHideConfirm((prev) => !prev)
      ),
    [hideConfirm]
  );

  return (
    <div
      className="tw:min-h-screen tw:bg-cover tw:bg-center tw:bg-no-repeat tw:font-['Lato'] tw:relative tw:flex tw:flex-col tw:items-center tw:justify-center tw:py-8 tw:px-6 tw:w-full"
      style={{ backgroundImage: `url(${SigninBg})` }}
    >
      {/* Language */}
      <div className="tw:absolute tw:top-6 tw:right-6">
        <LanguageDropdown />
      </div>

      {/* Logo */}
      <div className="tw:absolute tw:top-6 tw:left-6">
        <Logo />
      </div>

      {isVerifying && !isPasswordEnabled ? (
        <div className="tw:max-w-sm tw:w-full tw:p-6 tw:bg-white tw:rounded-2xl tw:shadow-lg tw:flex tw:flex-col tw:items-center tw:justify-center tw:transition-all tw:duration-500 tw:ease-in-out">
          <div className="tw:animate-spin tw:rounded-full tw:h-12 tw:w-12 tw:border-t-4 tw:border-b-4 tw:border-blue-500 tw:mb-4" />
          <h2 className="tw:text-lg tw:font-semibold tw:text-gray-700">
            {t(T.verifyingUser)}
          </h2>
          <p className="tw:text-gray-500 tw:mt-2 tw:text-center">
            {t(T.credentialVerifying)}
          </p>
        </div>
      ) : null}

      {isPasswordEnabled ? (
        <div className="tw:w-full tw:max-w-212 tw:mt-10.5   sm:tw:max-w-md md:tw:max-w-lg lg:tw:max-w-[848px] tw:rounded-xl tw:bg-primary tw:py-8 sm:tw:py-12 md:tw:py-16 lg:tw:py-20 tw:px-4 sm:tw:px-8 md:tw:px-16 lg:tw:px-[184px] tw:gap-6 sm:tw:gap-8 md:tw:gap-10 tw:flex tw:flex-col tw:items-center tw:justify-center tw:shadow-lg">
          {/* Header */}
          <Box className="tw:p-4 tw:gap-1">
            <div className="tw:flex tw:flex-col tw:bg-primary tw:text-text-secondary tw:text-center tw:w-full tw:gap-4 sm:tw:gap-6 md:tw:gap-10 tw:mb-4xl">
              <div className="tw:flex tw:flex-col tw:gap-1">
                <h1 className="tw:font-bold tw:text-3xl sm:tw:text-2xl md:tw:text-3xl tw:text-text-primary">
                  {t(T.setUpPassword)}
                </h1>

                <p className="tw:font-normal tw:text-[14px] sm:tw:text-base tw:text-text-secondary tw:leading-5">
                  {t(T.passwordGuide)}
                </p>
              </div>
            </div>

            <FormProvider {...methods}>
              <form
                className="tw:flex tw:flex-col tw:w-full tw:gap-3 sm:tw:gap-4"
                onSubmit={onSubmit}
                noValidate
              >
                <RHFInput<PasswordSettingFormValues>
                  name="password"
                  label={t(T.password)}
                  type={hidePassword ? 'text' : 'password'}
                  placeholder="*********"
                  rightIcon={passwordIcon}
                  autoComplete="new-password"
                  disabled={isLoading}
                />

                <div>
                  <RHFInput<PasswordSettingFormValues>
                    name="confirmPassword"
                    label={t(T.reEnterPassword)}
                    type={hideConfirm ? 'text' : 'password'}
                    placeholder="*********"
                    rightIcon={confirmIcon}
                    autoComplete="new-password"
                    disabled={isLoading}
                  />

                  <ul className="tw:flex tw:flex-col tw:gap-1 tw:mt-2 tw:list-none tw:p-0 tw:m-0">
                    {passwordCriteria.map(({ key, valid }) => (
                      <li
                        key={key}
                        className="tw:flex tw:items-center tw:text-[14px]  tw:gap-2 tw:text-sm"
                      >
                        {valid ? (
                          <CheckCircle
                            className="tw:shrink-0 tw:text-text-success"
                            height={16}
                            width={16}
                            aria-hidden
                          />
                        ) : (
                          <CrossCircle
                            className="tw:shrink-0 tw:text-text-error"
                            height={16}
                            width={16}
                            aria-hidden
                          />
                        )}
                        <span className="tw:text-text-secondary">{t(key)}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {isSuggested ? (
                  <RHFInput<PasswordSettingFormValues>
                    name="tenantDomain"
                    label={t(T.organizationSubDomain)}
                    placeholder={t(T.organizationSubDomainPlaceholder)}
                    leftText={SCHEME}
                    rightText={`.${POST_DOMAIN_PART}`}
                    helperText={
                      domainStatus === 'available' ? t(T.domainAvailable) : ''
                    }
                    helperIcon={
                      domainError
                        ? CrossCircle
                        : domainStatus === 'available'
                        ? CheckCircle
                        : undefined
                    }
                    disabled={isLoading}
                  />
                ) : null}

                <Button
                  type="submit"
                  className={`tw:w-full ${
                    isLoading
                      ? 'tw:disabled:bg-btn-brand tw:disabled:text-btn-primary-fg'
                      : ''
                  }`}
                  size="medium"
                  loading={isLoading}
                  text={t(T.continue)}
                  loadingText={t(LOADING_STEPS[stepIndex])}
                  spinnerColorClass={
                    isLoading ? 'tw:text-btn-primary-fg' : undefined
                  }
                />
                <div className="tw:mt-6 sm:tw:mt-8 md:tw:mt-10">
                  <InfoContainer />
                </div>
              </form>
            </FormProvider>
          </Box>
        </div>
      ) : null}

      {!isVerifying && !isPasswordEnabled ? (
        <div className=" tw:w-[50%] sm:tw:max-w-xl xs:tw:p-4 md:tw:max-w-[560px] tw:rounded-2xl tw:bg-primary tw:py-12 tw:px-12 sm:tw:py-12 sm:tw:px-6 md:tw:py-14 md:tw:px-12 tw:flex tw:flex-col tw:items-center tw:justify-center tw:shadow-lg">
          <div className="mb-3xl tw:w-14  tw:h-14 sm:tw:w-16 sm:tw:h-16 tw:rounded-full tw:bg-error tw:flex tw:items-center tw:justify-center tw:mb-6 xs:tw:mb-4">
            <img src={ErrorOutlineOutlinedIcon} alt="Error Icon" />
          </div>

          <Typography className="tw:font-semibold tw:text-3xl xs:tw:text-2xl md:tw:text-3xl tw:text-text-primary tw:text-center tw:mb-4">
            {t(T.verificationFailed)}
          </Typography>

          <Typography className="tw:text-text-secondary tw:text-center tw:font-['Lato'] tw:text-[14px] xs:tw:text-[12px] md:tw:text-[14px] tw:font-normal tw:leading-5 tw:tracking-[0] tw:mb-4">
            {t(T.verificationFailedDescription)}
          </Typography>

          <Button
            className="tw:w-full "
            size="medium"
            onClick={() => (window.location.href = '/')}
            text={t(T.returnToLogin)}
          />

          <div className="tw:mt-8">
            <InfoContainer />
          </div>
        </div>
      ) : null}
    </div>
  );
};
